import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { tokens } from "../../theme";
import { HEADER_OFFSET_PX, legalAccent } from "./LegalBlocks";

const TocEntries = ({ sections, activeId, onSelect }) => {
  const theme = useTheme();
  const accent = legalAccent(theme);

  return (
    <Stack component="ol" spacing={0} sx={{ listStyle: "none", m: 0, p: 0 }}>
      {sections.map((section, index) => {
        const isActive = section.id === activeId;

        return (
          <Box component="li" key={section.id}>
            <Box
              component="a"
              href={`#${section.id}`}
              aria-current={isActive ? "true" : undefined}
              onClick={(event) => onSelect(event, section.id)}
              sx={{
                display: "flex",
                gap: 1,
                py: 0.75,
                pl: 1.5,
                pr: 1,
                textDecoration: "none",
                fontSize: "0.9375rem",
                lineHeight: 1.5,
                borderLeft: "2px solid",
                borderColor: isActive ? accent : "transparent",
                color: isActive ? accent : theme.palette.text.secondary,
                fontWeight: isActive ? 600 : 400,
                transition: "color 0.2s ease-in-out, border-color 0.2s ease-in-out",
                "&:hover": { color: accent },
                "&:focus-visible": {
                  outline: `2px solid ${accent}`,
                  outlineOffset: "2px",
                  borderRadius: "2px",
                },
              }}
            >
              <Box component="span" sx={{ opacity: 0.75, minWidth: "1.4em" }}>
                {index + 1}.
              </Box>
              <Box component="span">{section.title}</Box>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * Section index for a legal document. On desktop it is a sticky sidebar that highlights the
 * section currently on screen; below `md` it collapses into an accordion above the text,
 * which closes itself once a section is chosen.
 */
const LegalTableOfContents = ({ sections, activeId, onNavigate }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expanded, setExpanded] = useState(false);

  const handleSelect = (event, id) => {
    onNavigate(event, id);
    setExpanded(false);
  };

  if (isMobile) {
    return (
      <Accordion
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={() => setExpanded((previous) => !previous)}
        sx={{
          backgroundColor: colors.background[300],
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          mb: 4,
          "&::before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="legal-toc-panel">
          <Typography variant="h5" sx={{ color: theme.palette.text.primary }}>
            Contents ({sections.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails id="legal-toc-panel" sx={{ pt: 0 }}>
          <Box component="nav" aria-label="Section navigation">
            <TocEntries sections={sections} activeId={activeId} onSelect={handleSelect} />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Box
      component="nav"
      aria-label="Section navigation"
      sx={{
        position: "sticky",
        top: `${HEADER_OFFSET_PX}px`,
        maxHeight: `calc(100vh - ${HEADER_OFFSET_PX + 32}px)`,
        overflowY: "auto",
        pb: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: theme.palette.text.primary,
          mb: 1.5,
          pl: 1.5,
        }}
      >
        Contents
      </Typography>
      {/* The 1px track is the rail the 2px active marker rides on; the negative margin
          pulls the entries left so the marker covers the track rather than sitting
          alongside it. */}
      <Box sx={{ borderLeft: `1px solid ${theme.palette.divider}`, "& > ol": { ml: "-1px" } }}>
        <TocEntries sections={sections} activeId={activeId} onSelect={handleSelect} />
      </Box>
    </Box>
  );
};

export default LegalTableOfContents;
